import {action} from 'mobx';
import s from '../s';
import {MouldingCustomization} from '../i';
import {statevent} from '../api';
import * as emitter from '../../global/emitter';
import {Portal, prod_entity_zoom_destroy} from '../../util';
import {scrollToPreview} from './preview-scroll';

// The moulding (frame) selection, shared by both product page layouts. Moved unchanged from components/moulding/moulding.tsx
// (the handler) and components/moulding/components/mouldings-list.tsx (the order the tiles show in).

export const selectMoulding = action((id: number): void => {
    prod_entity_zoom_destroy();
    s.ui.update_pricing = true;
    s.ps.moulding_id = id;
    const x = s.prod.mouldings.filter(xx => xx.id === id)[0];
    if (x) {
        s.ps.moulding_extWidth = x.extWidth;
        s.ui.moulding_th5 = x.th5 ? true : false;
        s.ui.moulding_th6 = x.th6 ? true : false;
    }

    if (s.prod.CustomizationMethod === 1) {
        s.ui.doRender = true;
    }

    s.pulse();

    scrollToPreview('prod-header');

    if (!s.prod.IsCAF && s.isRetail && id !== s.prod.MouldingID) {
        statevent({
            d1: 'cust_moulding',
            d2: s.prod.MouldingID.toString(),
            d3: id.toString(),
        });
    }

    if (s.prod.IsCAF && x && x.dyf_color === 'no gold' && s.ps.mat2_id === 385 && s.prod.caf_mat2.some(m => m.id === 398)) {
        emitter.eventEmitter.dispatch(emitter.EventType.MOULDING_SILVER_ONLY, 'mat');
    }

    if (s.prod.IsCAF && x && x.dyf_color === 'no silver' && s.ps.mat2_id === 398 && s.prod.caf_mat2.some(m => m.id === 385)) {
        emitter.eventEmitter.dispatch(emitter.EventType.MOULDING_GOLD_ONLY, 'mat');
    }
});

// ---- view model ------------------------------------------------------------------------------------------------------

/** product.tsx's mount rule for the moulding section. */
export const mouldingSectionMounted = (): boolean => !s.IsPortal3 && !!s.prod && !!s.prod.mouldings && s.prod.mouldings.length > 1;

/** The section shows: mounted, and Moulding's own guard. */
export const mouldingSectionShown = (): boolean => mouldingSectionMounted() && s.ready && s.ui.show_cust && !!s.prod.mouldings;

const ALPHABETIZE_ENABLED = false;

/**
 * The order the moulding tiles show in: the product's own moulding first, then the trending one (diplomaframe.com only, never on a
 * Fast Ship product, never the product's own moulding), then the rest. hasFastShip marks the product's own moulding with the
 * Fast Ship icon; trendingMoulding carries the Trending label.
 */
export const mouldingOrder = (filtered: MouldingCustomization[]) => {
    const hasFastShip = s.__fast_ship_orig && s.isRetail && filtered.some(m => s.prod.MouldingID === m.id);
    const originalMoulding = filtered.find(m => m.isBase);
    let trendingMouldings = filtered.filter(m => m.IsTrending);

    let trendingMoulding: MouldingCustomization | undefined;

    if (Portal && Portal.PortalID > 0) {
        trendingMouldings = [];
    }

    if (!hasFastShip && trendingMouldings.length > 0) {
        trendingMoulding = trendingMouldings.sort((a, b) => {
            const aScore = a.Customizations365 ?? -1;
            const bScore = b.Customizations365 ?? -1;

            if (aScore !== bScore) return bScore - aScore;

            return a.name.localeCompare(b.name);
        })[0];
    }

    if (Portal && Portal.PortalID > 0) {
        trendingMoulding = undefined;
    }

    const remaining = filtered.filter(m => m !== originalMoulding && m !== trendingMoulding).filter((m, index, self) => index === self.findIndex(other => other.id === m.id));
    const trendingRemaining = ALPHABETIZE_ENABLED ? [...remaining].sort((a, b) => a.name.localeCompare(b.name)) : remaining;
    const sortedMouldings = [...(originalMoulding ? [originalMoulding] : []), ...(trendingMoulding && trendingMoulding.id !== s.prod.MouldingID ? [trendingMoulding] : []), ...trendingRemaining];

    return {hasFastShip, trendingMoulding, sortedMouldings};
};

/** The Trending label on a tile (MouldingsList's rule). */
export const isTrendingMoulding = (m: MouldingCustomization, order: {hasFastShip: boolean; trendingMoulding?: MouldingCustomization}): boolean => !order.hasFastShip && m === order.trendingMoulding && m.id !== s.prod.MouldingID;

/** The Fast Ship icon on a tile: the product's own moulding on a Fast Ship product. */
export const isFastShipMoulding = (m: MouldingCustomization): boolean => s.__fast_ship_orig && s.isRetail && s.prod.MouldingID === m.id;
